import calendarAndSlotsReducer from './slices/calendarAndSlots';
import { configureStore, AnyAction, ThunkAction } from '@reduxjs/toolkit';
import { persistStore, persistReducer, PersistConfig } from 'redux-persist';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import storage from 'redux-persist/lib/storage';
import auth from './slices/auth';
import salesOrdersReducer from './slices/salesOrders';
import customersReducer from './slices/customers';
import countriesReducer from './slices/countries';
import workOrderServiceConsultationReducer from './slices/workOrderServiceConsultation';
import workOrderServiceDiagnosticReducer from './slices/workOrderServiceDiagnostic';
import consultationFindingsReducer from './slices/consultationFindings';
import consultationInvestigationsReducer from './slices/consultationInvestigations';
import consultationRecommendationsReducer from './slices/consultationRecommendations';
import consultationNotesReducer from './slices/consultationNotes';
import diagnosticResultsReducer from './slices/diagnosticResults';
import samplesTakenReducer from './slices/samplesTaken';
import gendersReducer from './slices/gender';
import nationalitiesReducer from './slices/nationalities';
import checkInReducer from './slices/checkIn';
import cartReducer from './slices/cart';
import salesOrderProducts from './slices/salesOrderProducts';
import documentsReducer from './slices/documents';
import servicesReducer from './slices/services';
import productCategoriesReducer from './slices/productCategories';
import doctorsReducer from './slices/doctors';
import vitalSignsReducer from './slices/vitalSigns';
import chatReducer from './slices/chat';
import examinationsReducer from './slices/examinations';
import sickLeaveAndPrescriptionReducer from './slices/sickLeaveAndPrescription';
import otherServicesReducer from './slices/consultationOtherServices';
import notificationsReducer from './slices/notifications';

const persistConfigAuth: PersistConfig<any> = {
  key: 'auth',
  storage,
};

const rootReducer = {
  auth: persistReducer(persistConfigAuth, auth),
  salesOrders: salesOrdersReducer,
  customers: customersReducer,
  countries: countriesReducer,
  workOrderServiceConsultation: workOrderServiceConsultationReducer,
  workOrderServiceDiagnostic: workOrderServiceDiagnosticReducer,
  consultationFindings: consultationFindingsReducer,
  consultationInvestigations: consultationInvestigationsReducer,
  consultationRecommendations: consultationRecommendationsReducer,
  consultationNotes: consultationNotesReducer,
  diagnosticResults: diagnosticResultsReducer,
  calendarAndSlots: calendarAndSlotsReducer,
  samplesTaken: samplesTakenReducer,
  genders: gendersReducer,
  nationalities: nationalitiesReducer,
  checkIn: checkInReducer,
  cart: cartReducer,
  salesOrderProducts: salesOrderProducts,
  documents: documentsReducer,
  services: servicesReducer,
  productCategories: productCategoriesReducer,
  doctors: doctorsReducer,
  vitalSigns: vitalSignsReducer,
  chat: chatReducer,
  examinations: examinationsReducer,
  sickLeaveAndPrescription: sickLeaveAndPrescriptionReducer,
  otherServices: otherServicesReducer,
  notifications: notificationsReducer,
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

export const persistor = persistStore(store);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;

export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, AnyAction>;

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
